import React, {useState} from 'react';
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import {Close} from '@mui/icons-material';
import {WorkScheduleForm} from './WorkScheduleForm';
import {getImageUrl} from '../helpers/getImageUrl';

const BranchForm = ({
    initialData = {},
    onSubmit,
    onDelete,
    onCancel,
    isLoading = false,
    submitButtonText = 'Сохранить',
}) => {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        name: initialData.name || '',
        misName: initialData.misName || '',
        reviewLinksConfig: initialData.reviewLinksConfig || {links: {}, order: []},
        address: initialData.address || '',
        phone: initialData.phone || '',
        isActive: initialData.isActive || false,
        workSchedule: initialData.workSchedule || {},
        mapImages: [],
        timezoneOffset: typeof initialData.timezoneOffset === 'number'
            ? initialData.timezoneOffset
            : -180,
        hasOwnSystem: initialData.hasOwnSystem === true,
        apiKey: initialData.systemSecrets?.apiKey || '',
        webApiAddress: initialData.systemSecrets?.webApiAddress || '',
        showHowToGetButton: initialData.showHowToGetButton === true,
        howToGetDescription: initialData.howToGetDescription || '',
    });

    const [existingImages, setExistingImages] = useState(
        Array.isArray(initialData.mapImageName) ? initialData.mapImageName : []
    );

    const [imagePreviews, setImagePreviews] = useState([]);
    const [hasScheduleError, setHasScheduleError] = useState(false);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
    };

    const handleTimezoneChange = (e) => {
        const hours = parseInt(e.target.value, 10);
        setFormData((prev) => ({...prev, timezoneOffset: -60 * hours}));
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const totalImages = existingImages.length + formData.mapImages.length + files.length;
        if (totalImages > 10) {
            alert(
                `Можно загрузить максимум 10 изображений. Сейчас: ${existingImages.length} существующих, ${formData.mapImages.length} новых. Доступно мест: ${
                    10 - existingImages.length - formData.mapImages.length
                }`
            );
            return;
        }

        for (const file of files) {
            if (file.size > 5 * 1024 * 1024) {
                alert('Размер каждого файла не должен превышать 5 МБ');
                return;
            }
        }

        setFormData((prev) => ({
            ...prev,
            mapImages: [...prev.mapImages, ...files],
        }));

        const newPreviews = files.map((file) => ({
            file,
            url: URL.createObjectURL(file),
        }));
        setImagePreviews((prev) => [...prev, ...newPreviews]);
    };

    const removeExistingImage = (imgName) => {
        setExistingImages((prev) => prev.filter((img) => img !== imgName));
    };

    const removeNewImage = (index) => {
        setFormData((prev) => ({
            ...prev,
            mapImages: prev.mapImages.filter((_, i) => i !== index),
        }));
        setImagePreviews((prev) => {
            if (prev[index]) {
                URL.revokeObjectURL(prev[index].url);
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const removeAllImages = () => {
        if (window.confirm('Удалить все изображения?')) {
            setExistingImages([]);
            setFormData((prev) => ({...prev, mapImages: []}));
            imagePreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
            setImagePreviews([]);
        }
    };

    const handleSubmit = async () => {
        await onSubmit({
            formData,
            existingImages,
            imagePreviews,
            setExistingImages,
            setImagePreviews,
            setFormData,
        });
    };

    const isFormValid =
        formData.name && formData.misName && formData.address && formData.phone;

    const formFields = [
        {key: 'name', label: 'Название филиала в боте', required: true},
        {
            key: 'misName',
            label: 'Название филиала в МИС',
            required: true,
            helperText: 'Название должно соответствовать названию филиала в вашей МИС',
        },
        {key: 'address', label: 'Адрес филиала', required: true},
        {key: 'phone', label: 'Телефон', required: true},
    ];

    return (
        <Box>
            <Grid container spacing={2} sx={{pt: 1}}>
                {/* Основные поля в 2 колонки */}
                {formFields.map((field) => (
                    <Grid item xs={12} md={6} key={field.key}>
                        <TextField
                            label={field.label}
                            fullWidth
                            required={field.required}
                            variant="outlined"
                            size="small"
                            InputLabelProps={{shrink: true}}
                            name={field.key}
                            type={field.type || 'text'}
                            value={formData[field.key]}
                            onChange={handleInputChange}
                            error={field.required && !formData[field.key]}
                            helperText={
                                field.helperText
                                    ? field.helperText
                                    : field.required && !formData[field.key]
                                        ? 'Обязательное поле'
                                        : ' '
                            }
                            sx={{
                                '& .MuiInputBase-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: theme.palette.background.paper,
                                },
                            }}
                        />
                    </Grid>
                ))}

                {/* Часовой пояс */}
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth required size="small">
                        <InputLabel id="timezone-offset-label">Смещение от UTC (часы)</InputLabel>
                        <Select
                            labelId="timezone-offset-label"
                            id="timezone-offset-select"
                            name="timezoneOffset"
                            value={-formData.timezoneOffset / 60}
                            label="Смещение от UTC (часы)"
                            onChange={handleTimezoneChange}
                        >
                            {[...Array(25)].map((_, i) => {
                                const hour = i - 12;
                                const label = hour > 0 ? `+${hour}` : hour;
                                return (
                                    <MenuItem key={hour} value={hour}>
                                        {label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>

                <Divider sx={{width: '100%', mt: 2}}/>


                {/* Переключатели в одной строке */}
                <Grid item xs={12}>
                    <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap'}}>
                        {initialData.id && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) =>
                                            setFormData((prev) => ({...prev, isActive: e.target.checked}))
                                        }
                                        color="primary"
                                    />
                                }
                                label="Вкл/Выкл работы филиала"
                            />
                        )}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.hasOwnSystem}
                                    onChange={(e) =>
                                        setFormData((prev) => ({...prev, hasOwnSystem: e.target.checked}))
                                    }
                                    color="primary"
                                />
                            }
                            label="Собственная система"
                        />
                    </Box>
                </Grid>

                {/* Credentials в 2 колонки */}
                {formData.hasOwnSystem && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                ℹ️ Укажите credentials для подключения к отдельной системе филиала
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="API Key"
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputLabelProps={{shrink: true}}
                                name="apiKey"
                                value={formData.apiKey}
                                onChange={handleInputChange}
                                placeholder="Введите API ключ"
                                sx={{
                                    '& .MuiInputBase-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: theme.palette.background.paper,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Web API Address"
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputLabelProps={{shrink: true}}
                                name="webApiAddress"
                                value={formData.webApiAddress}
                                onChange={handleInputChange}
                                placeholder="https://api.example.com"
                                sx={{
                                    '& .MuiInputBase-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: theme.palette.background.paper,
                                    },
                                }}
                            />
                        </Grid>
                    </>
                )}

                <Divider sx={{width: '100%', mt: 2}}/>


                {/* Изображения и маршрут */}
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{mb: 1.5, fontWeight: 600}}>
                        Схемы проезда к филиалу
                    </Typography>

                    <Box sx={{mb: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap'}}>
                        <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            disabled={existingImages.length + formData.mapImages.length >= 10}
                        >
                            Добавить
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                multiple
                                onChange={handleImageChange}
                            />
                        </Button>
                        {(existingImages.length > 0 || formData.mapImages.length > 0) && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={removeAllImages}
                            >
                                Удалить все
                            </Button>
                        )}
                        <Typography variant="caption" color="textSecondary">
                            {existingImages.length + formData.mapImages.length} / 10
                        </Typography>
                    </Box>

                    {existingImages.length > 0 && (
                        <Box sx={{mb: 2}}>
                            <Typography variant="caption" color="textSecondary" sx={{mb: 0.5, display: 'block'}}>
                                Текущие:
                            </Typography>
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                {existingImages.map((imgName, index) => (
                                    <Box key={imgName} sx={{position: 'relative', width: 80}}>
                                        <img
                                            src={getImageUrl(imgName)}
                                            alt={`Схема ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: 80,
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                                border: '1px solid #ddd',
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            onClick={() => removeExistingImage(imgName)}
                                            sx={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 2,
                                                minWidth: 0,
                                                p: 0.25,
                                                minHeight: 0,
                                            }}
                                        >
                                            <Close fontSize="small" sx={{fontSize: 14}}/>
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {imagePreviews.length > 0 && (
                        <Box sx={{mb: 2}}>
                            <Typography variant="caption" color="textSecondary" sx={{mb: 0.5, display: 'block'}}>
                                {initialData.id ? 'Новые:' : 'Выбранные:'}
                            </Typography>
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                {imagePreviews.map((preview, index) => (
                                    <Box key={index} sx={{position: 'relative', width: 80}}>
                                        <img
                                            src={preview.url}
                                            alt={`${initialData.id ? 'Новое' : 'Схема'} ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: 80,
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                                border: '2px solid #4caf50',
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            onClick={() => removeNewImage(index)}
                                            sx={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 2,
                                                minWidth: 0,
                                                p: 0.25,
                                                minHeight: 0,
                                            }}
                                        >
                                            <Close fontSize="small" sx={{fontSize: 14}}/>
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Grid>

                {/* Описание "Как добраться" */}
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{mb: 1.5, fontWeight: 600}}>
                        Отображать кнопку в боте “Как добраться” в разделе “О клинике”
                    </Typography>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.showHowToGetButton}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        showHowToGetButton: e.target.checked,
                                    }))
                                }
                                color="primary"
                                size="small"
                            />
                        }
                        label="Вкл/Выкл “Как добраться”"
                        sx={{mb: 1}}
                    />

                    {formData.showHowToGetButton && (
                        <TextField
                            label="Описание маршрута"
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            size="small"
                            InputLabelProps={{shrink: true}}
                            name="howToGetDescription"
                            value={formData.howToGetDescription}
                            onChange={handleInputChange}
                            placeholder="Введите описание маршрута к филиалу..."
                            helperText="Опционально"
                            sx={{
                                '& .MuiInputBase-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: theme.palette.background.paper,
                                },
                            }}
                        />
                    )}
                </Grid>

                <Divider sx={{width: '100%', mt: 2}}/>


                {/* Расписание работы */}
                <Grid item xs={12}>
                    <WorkScheduleForm
                        formData={formData}
                        setFormData={setFormData}
                        onErrorChange={setHasScheduleError}
                    />
                </Grid>

                <Divider sx={{width: '100%', mt: 2}}/>


                {/* Кнопки действий */}
                <Grid item xs={12}>
                    <Box sx={{display: 'flex', gap: 2, mt: 1, justifyContent: 'space-between', alignItems: 'center'}}>
                        <Box sx={{display: 'flex', gap: 2}}>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={!isFormValid || isLoading || hasScheduleError}
                                sx={{
                                    px: 4,
                                    borderRadius: 2,
                                    bgcolor: theme.palette.success.main,
                                    '&:hover': {bgcolor: theme.palette.success.dark},
                                }}
                            >
                                {isLoading ? 'Сохранение...' : submitButtonText}
                            </Button>
                            {onCancel && (
                                <Button
                                    variant="outlined"
                                    onClick={onCancel}
                                    disabled={isLoading}
                                    sx={{
                                        px: 4,
                                        borderRadius: 2,
                                    }}
                                >
                                    Отмена
                                </Button>
                            )}
                        </Box>
                        {onDelete && initialData.id && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => onDelete(initialData.id)}
                                disabled={isLoading}
                                sx={{
                                    borderRadius: 2,
                                }}
                            >
                                Удалить филиал
                            </Button>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BranchForm;
